import { randomString } from '../../../../utilities/randomUtilities';
import { getEditHistoryEnabled } from '../../../../utilities/storageUtilities';
import types from './types';

export const previewHistoryBatch = {
    group: null,

	// Start a new group to track all the actions as a single step
    start() {
		if (!this.group) {
			this.group = randomString();
		}
	},
	
	// Stop tracking the actions for a current group
    end() {
		this.group = null;
	},
	
	refresh() {
		this.group = randomString();
	},

	// Returns the group name for undo/redo reducer
	// The returned function can have next parameters: (action, currentState, previousHistory) => {}
    init() {
		return () => this.group;
    }
};

export const previewHistoryFilter = {
	historyTrackingEnabled: false,

	// Kept separate from historyTrackingEnabled: bulk-load flows toggle that flag
	// freely, and must not re-enable recording while the edit history setting is off.
	editHistoryDisabled: !getEditHistoryEnabled(),

	enableHistoryTracking() {
		this.historyTrackingEnabled = true;
	},

	disableHistoryTracking() {
		this.historyTrackingEnabled = false;
	},

	setEditHistoryEnabled(enabled) {
		this.editHistoryDisabled = !enabled;
	},

	// Returns the conditions for actions tracking
    init() {
		return (action, currentState, previousHistory) => {
			if (this.editHistoryDisabled)
				return false;

			if (action.type === types.SET_PREVIEW_PROPERTY_WITHOUT_TRACKING)
				return false; 

			if (!action.type.includes('CREATE_PREVIEW')) 
				return false;

			return this.historyTrackingEnabled;
		};
    }
};

// Tracks the in-flight Direct Upload operations. Lives outside Redux because an
// AbortController is not serializable, and outside the thunks so CreatePage can
// deterministically reset it on mount.
export const directUploadTracker = {
	backgroundUploadAbortController: null,
	inFlightImageUploads: 0,

	// A newly picked background video supersedes the previous pick, so the earlier
	// in-flight upload is cancelled and only the latest registers its controller.
	replaceBackgroundUpload(abortController) {
		this.backgroundUploadAbortController?.abort();
		this.backgroundUploadAbortController = abortController;
	},

	// Clears the slot only when it still belongs to the finished upload; returns
	// whether it did, so a superseded upload does not hide the newer one's overlay.
	finishBackgroundUpload(abortController) {
		if (this.backgroundUploadAbortController !== abortController)
			return false;

		this.backgroundUploadAbortController = null;
		return true;
	},

	beginImageUpload() {
		this.inFlightImageUploads++;
	},

	// Returns true when the last in-flight image upload has finished. Clamped at
	// zero so a settle landing after reset() cannot drive the counter negative.
	finishImageUpload() {
		this.inFlightImageUploads = Math.max(0, this.inFlightImageUploads - 1);
		return this.inFlightImageUploads === 0;
	},

	// Entering the create page starts from a clean slate regardless of how the
	// previous visit ended.
	reset() {
		this.backgroundUploadAbortController?.abort();
		this.backgroundUploadAbortController = null;
		this.inFlightImageUploads = 0;
	}
};
